<script setup lang="ts">
import { FunctionalComponent } from 'vue';
import { Card } from '@Shared/types/portal/dashboard';
import { useScreen } from '@Shared/utils/useScreen';
import VerticalCutoffText from './VerticalCutoffText.vue';
import dayjs from 'dayjs';
import updateLocalePlugin from 'dayjs/plugin/updateLocale';
import relativeTimePlugin from 'dayjs/plugin/relativeTime';

import NpsPromoterInverted from '../visualAssets/NpsPromoterInverted.svg?component';
import NpsNeutralInverted from '../visualAssets/NpsNeutralInverted.svg?component';
import NpsDetractorInverted from '../visualAssets/NpsDetractorInverted.svg?component';
import NpsEmptyInverted from '../visualAssets/NpsEmptyInverted.svg?component';

const props = defineProps<{ card: Card }>();

const cleanFooters = computed<{ title: string; value: string }[]>(() => {
  return props.card.footerFields.filter((f) => f.value) as {
    title: string;
    value: string;
  }[];
});

// Everytime the screen changes we animate, this is to avoid the cards getting stuck in weird positions.
const screen = useScreen();

const icon = computed<FunctionalComponent>(() => {
  if (props.card.metricValue > 0) return NpsPromoterInverted;
  else if (props.card.metricValue < 0) return NpsDetractorInverted;
  else if (props.card.metricValue == 0) return NpsNeutralInverted;
  else return NpsEmptyInverted;
});
dayjs.extend(updateLocalePlugin);
dayjs.extend(relativeTimePlugin);

dayjs.updateLocale('en', {
  relativeTime: {
    future: 'in %s',
    past: '%s ago',
    s: 'a few seconds',
    m: '1 minute',
    mm: '%d minutes',
    h: '1 hour',
    hh: '%d hours',
    d: '1 day',
    dd: '%d days',
    M: '1 month',
    MM: '%d months',
    y: '1 year',
    yy: '%d years',
  },
});
const date = computed<string>(() => {
  return dayjs(props.card.dateTimeField).from(dayjs(import.meta.env.VITE_DATE_ANCHOR));
});

const bgColorHeader = computed<string>(() => {
  if (props.card.metricValue > 0) return 'bg-[#097152]';
  else if (props.card.metricValue < 0) return 'bg-[#B20125]';
  else if (props.card.metricValue == 0) return 'bg-[#7F7466]';
  else return 'bg-[#bdc4d1]';
});

const fontSize = computed<string>(() => {
  // based on the screen size and the number of characters we change the font size of the verbatim
  let size = 0;
  // smallest screen size
  if (screen.value.width < 1920) {
    if (props.card.verbatim.length < 92) {
      size = 1.75; // 28px
    } else {
      size = 1.25; // 20px
    }
  } // middle screen size
  else if (screen.value.width < 3840) {
    if (props.card.verbatim.length < 92) {
      size = 2.75; // 44px
    } else {
      size = 2.5; // 40px
    }
  } // largest screen size
  else {
    if (props.card.verbatim.length < 95) {
      size = 5; // 80px
    } else {
      size = 4.5; // 72px
    }
  }
  return `${size}rem`;
});

const cardRef = ref<HTMLElement | null>(null);
</script>
<template>
  <div class="relative" ref="cardRef">
    <div
      class="border-solid h-[280px] xl:h-[320px] 2xl:h-[482px] 3xl:h-[964px] xl:border-4 3xl:border-12 rounded-2.5 shadow-md flex flex-col bg-white border-[#D5D7DB]"
    >
      <div
        :class="bgColorHeader"
        class="xl:pt-2 xl:pb-2 xl:pl-10 xl:pr-10 2xl:pt-6 2xl:pb-6 2xl:pl-10 2xl:pr-10 3xl:pl-16 3xl:pr-16 flex flex-row h-auto items-center rounded-[4px_4px_0_0]"
      >
        <component class="w-10 2xl:w-14 3xl:w-26" :is="icon"></component>
        <div class="inline-block pl-5 2xl:pl-6 3xl:pl-9 align-sub 2xl:align-top">
          <p
            class="font-bold text-white text-xs mt-2 mb-2 xl:text-xl 2xl:text-3xl 2xl:mt-6 2xl:mb-6 3xl:text-3.75rem 3xl:line-height-5rem"
          >
            {{ card.programField }}
          </p>
        </div>
        <p class="text-white ml-auto font-semibold text-sm xl:text-4 2xl:text-6 3xl:text-12">
          {{ date }}
        </p>
      </div>
      <div
        class="xl:pl-10 xl:pr-10 xl:mt-4 2xl:mt-6 min-h-0 mb-auto 2xl:pl-10 2xl:pr-10 3xl:pt-20 3xl:pb-20 3xl:pl-20 3xl:pr-20"
      >
        <VerticalCutoffText
          :content="card.verbatim"
          class="overflow-hidden font-semibold"
          style="line-height: 125%"
          :style="{
            'font-size': fontSize,
          }"
        />
      </div>
      <div
        class="border-solid border-0 border-t-2 border-[#D5D7DB] pl-10 pr-10 xl:pb-4 2xl:text-6 rounded-[0_0_4px_4px] pb-3 2xl:pb-6 3xl:pb-10 3xl:pl-20 3xl:pr-20 3xl:border-t-4"
      >
        <div class="flex flex-row space-x-1rem xl:space-x-2rem 2xl:space-x-2.5rem 3xl:space-x-5rem overflow-hidden">
          <div v-for="footerField in cleanFooters" :key="footerField.title">
            <div
              class="uppercase whitespace-nowrap pt-4 pr-3 2xl:pr-4 3xl:pr-4 pb-1 xl:text-2.5 2xl:text-4 3xl:text-9 text-[#4F4F4F] border-b-2 border-[#CFD6E2] 3xl:pb-2"
            >
              {{ footerField.title }}
            </div>
            <div
              :style="{
                'max-width': (cardRef ? cardRef.offsetWidth * 0.4 : 100) + 'px',
              }"
              class="font-semibold text-[#333333] truncate pt-1 max-w-100% xl:text-base 2xl:text-2xl 3xl:text-5xl 3xl:pt-4 3xl:pb-2"
            >
              {{ footerField.value }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
